import type { Variables } from "@/lib/sparxs/metadataSchemas";
import type { ParsedDirectiveRaw } from "./parseRaw";
import type { DirectiveConfigItem, DirectiveChoiceSlot } from "./config";
import {
  getTableVariableColumns,
  getPrintTableChoiceSlotForIndex,
} from "./config";

export interface DirectiveChipStrategy {
  getChoiceSlotsCount: (
    parsed: ParsedDirectiveRaw,
    config: DirectiveConfigItem,
    allVariables: Variables[],
  ) => number;

  getNextChoiceSlot: (
    parsed: ParsedDirectiveRaw,
    config: DirectiveConfigItem,
    allVariables: Variables[],
    nextChoiceIndex: number,
    currentChoices: string[],
  ) => DirectiveChoiceSlot | undefined;

  getChoiceSlotForEdit: (
    parsed: ParsedDirectiveRaw,
    config: DirectiveConfigItem,
    allVariables: Variables[],
    choiceIndex: number,
    choicesPart: string[],
  ) => DirectiveChoiceSlot | undefined;

  isCompleteAfterVariableSelect: (
    variableName: string,
    parsed: ParsedDirectiveRaw,
    config: DirectiveConfigItem,
    allVariables: Variables[],
  ) => boolean;

  getChoiceSlotLabel: (
    choiceIndex: number,
    parsed: ParsedDirectiveRaw,
    config: DirectiveConfigItem,
    allVariables: Variables[],
    choicesPart: string[],
  ) => string;

  getSlotAndMaxForChoiceSubmit: (
    parsed: ParsedDirectiveRaw,
    config: DirectiveConfigItem,
    allVariables: Variables[],
    nextIndex: number,
  ) => { slot: DirectiveChoiceSlot | undefined; maxSlots: number };

  allowOptionalSkip: (
    slot: DirectiveChoiceSlot | undefined,
    value: string,
  ) => boolean;
}

export const printTableDirectiveStrategy: DirectiveChipStrategy = {
  getChoiceSlotsCount: (parsed, _config, allVariables) => {
    const cols = getTableVariableColumns(allVariables, parsed.variable);
    return cols != null ? cols.length : 0;
  },

  getNextChoiceSlot: (
    parsed,
    _config,
    allVariables,
    nextChoiceIndex,
    currentChoices,
  ) => {
    const cols = getTableVariableColumns(allVariables, parsed.variable);
    if (cols == null) return undefined;
    return getPrintTableChoiceSlotForIndex(
      cols,
      currentChoices,
      nextChoiceIndex,
    );
  },

  getChoiceSlotForEdit: (
    parsed,
    _config,
    allVariables,
    choiceIndex,
    choicesPart,
  ) => {
    const cols = getTableVariableColumns(allVariables, parsed.variable);
    if (cols == null) return undefined;
    return getPrintTableChoiceSlotForIndex(cols, choicesPart, choiceIndex);
  },

  isCompleteAfterVariableSelect: (
    variableName,
    _parsed,
    _config,
    allVariables,
  ) => {
    const cols = getTableVariableColumns(allVariables, variableName);
    return !(cols != null && cols.length > 0);
  },

  getChoiceSlotLabel: (
    choiceIndex,
    parsed,
    _config,
    allVariables,
    choicesPart,
  ) => {
    const cols = getTableVariableColumns(allVariables, parsed.variable);
    if (cols == null) return `Choice ${choiceIndex + 1}`;
    return getPrintTableChoiceSlotForIndex(cols, choicesPart, choiceIndex)
      .label;
  },

  getSlotAndMaxForChoiceSubmit: (parsed, _config, allVariables, nextIndex) => {
    const cols = getTableVariableColumns(allVariables, parsed.variable);
    if (cols == null) return { slot: undefined, maxSlots: 0 };
    const slot = getPrintTableChoiceSlotForIndex(
      cols,
      parsed.choices,
      nextIndex,
    );
    return { slot, maxSlots: cols.length };
  },

  allowOptionalSkip: (_slot, value) => value.trim() === "",
};
